import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Box } from '@material-ui/core';

import Modal from 'features/app/components/Modal';
import { useTranslation } from 'hooks/useTranslation';
import useValidation from 'hooks/useValidation';
import { SelectInputField } from 'features/app/components/SelectInputField';
import BaseButton from 'features/app/components/BaseButton';
import { PartnerUser } from 'features/users/ts/PartnerUser';
import { confirmDialog } from 'features/app/utils/confirmDialog';

import { useStyles } from './styles';
import { StringMap } from 'i18next';
import i18n from 'locales/i18n';
import { useLanguage } from 'hooks/useLanguage';

interface EditProfileModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (user: PartnerUser) => Promise<unknown>;
}

interface SelectLanguageFormState extends PartnerUser {
  language: StringMap;
}

export const ChangeLanguageModal: React.FC<EditProfileModalProps> = ({ onClose, open }) => {
  const { t } = useTranslation('app');
  const { languages, currentLanguageName } = useLanguage();
  const { required } = useValidation();
  const classes = useStyles();

  const form = useForm<SelectLanguageFormState>({
    mode: 'onChange',
    defaultValues: {
      language: {
        name: currentLanguageName,
      },
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = form;

  const onSubmit = React.useCallback(
    values => {
      if (values.language.name === currentLanguageName) {
        onClose();
        return;
      }

      if (values) {
        i18n.changeLanguage(values.language.code);
        onClose();
        location.reload();
      }
    },
    [currentLanguageName, onClose]
  );

  const handleClose = React.useCallback(async () => {
    let shouldClose: undefined | boolean = true;

    if (isDirty) {
      shouldClose = await confirmDialog({ title: t('formLeavingPrompt', { ns: 'common' }) });
    }

    if (shouldClose) {
      onClose();
    }
  }, [onClose, t, isDirty]);

  return (
    <Modal open={open} onClose={handleClose} title={t('changeLanguage')} paperClass={classes.container}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
          <Box>
            <SelectInputField
              name="language"
              label={t('language')}
              getOptionLabel={o => `${o.name}`}
              options={languages}
              maxLength={1}
              rules={{
                validate: {
                  ...required,
                },
              }}
            />
          </Box>
          <Box className={classes.buttonsContainer}>
            <Box width="45%" display="flex">
              <BaseButton variant="text" className={classes.cancelButton} onClick={handleClose}>
                {t('cancel', { ns: 'common' })}
              </BaseButton>
              <BaseButton className={classes.saveButton} type="submit" isLoading={isSubmitting}>
                {t('save', { ns: 'common' })}
              </BaseButton>
            </Box>
          </Box>
        </form>
      </FormProvider>
    </Modal>
  );
};
